import { useState, useEffect, useCallback, useRef } from 'react';

/** Stable id for queue dedup (stepId preferred over legacy title). */
function tutorialStepId(tutorial) {
  return tutorial?.stepId || tutorial?.id || tutorial?.title || null;
}

/** Merged into counter_drones (mission 3 turn 4); skip if still present in stale queues. */
const DEPRECATED_TUTORIAL_STEP_IDS = new Set(['protect_your_units']);

function isDeprecatedTutorial(tutorial) {
  const id = tutorialStepId(tutorial);
  return id != null && DEPRECATED_TUTORIAL_STEP_IDS.has(id);
}

/**
 * Campaign State Hook
 * Manages campaign-specific state including tutorial queue
 */
export function useCampaignState(gameState) {
  const [tutorialQueue, setTutorialQueue] = useState([]);
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  
  // Track tutorials that have been shown (to prevent re-adding from backend queue)
  const shownTutorialsRef = useRef(new Set());
  
  // Expose currentTutorial title for addTutorial to check
  const currentTutorialTitleRef = useRef(null);
  useEffect(() => {
    currentTutorialTitleRef.current = currentTutorial?.stepId || currentTutorial?.title || null;
    // Track when a tutorial is shown
    const tutorialId = currentTutorial?.stepId || currentTutorial?.title;
    if (tutorialId) {
      shownTutorialsRef.current.add(tutorialId);
    }
  }, [currentTutorial]);

  // Check for new tutorials in game state
  useEffect(() => {
    if (gameState?.campaignMode && gameState?.scriptState?.tutorialQueue) {
      const newTutorials = gameState.scriptState.tutorialQueue;
      console.log(`📚 useCampaignState: Detected ${newTutorials.length} tutorial(s) in queue:`, newTutorials);
      if (newTutorials.length > 0) {
        // Add new tutorials to queue, but skip ones that have already been shown
        setTutorialQueue(prev => {
          const existingIds = new Set(prev.map(t => t.stepId || t.id || t.title));
          const toAdd = newTutorials.filter(t => {
            const id = t.stepId || t.id || t.title;
            // Skip if already in queue, currently showing, already been shown, or deprecated
            return !existingIds.has(id) && 
                   !shownTutorialsRef.current.has(id) &&
                   id !== currentTutorialTitleRef.current &&
                   !DEPRECATED_TUTORIAL_STEP_IDS.has(id);
          });
          console.log(`📚 useCampaignState: Adding ${toAdd.length} new tutorial(s) to queue (skipped ${newTutorials.length - toAdd.length} already shown)`);
          return [...prev, ...toAdd].filter(t => !isDeprecatedTutorial(t));
        });
        
        // Clear tutorial queue in game state (frontend manages it now)
        // Note: This is a local copy, actual gameState is managed by backend
      }
    } else {
      console.log(`📚 useCampaignState: No tutorials found. campaignMode: ${gameState?.campaignMode}, hasScriptState: ${!!gameState?.scriptState}, tutorialQueue: ${gameState?.scriptState?.tutorialQueue}`);
    }
  }, [gameState?.scriptState?.tutorialQueue, gameState?.campaignMode]);

  // Show next tutorial when queue has items and no current tutorial
  useEffect(() => {
    console.log(`📚 useCampaignState: Tutorial display effect. Queue length: ${tutorialQueue.length}, currentTutorial: ${!!currentTutorial}, isPaused: ${isPaused}`);
    if (tutorialQueue.length > 0 && !currentTutorial && !isPaused) {
      const nextTutorial = tutorialQueue[0];
      console.log(`📚 useCampaignState: Showing tutorial:`, nextTutorial);
      setCurrentTutorial(nextTutorial);
      setIsPaused(true);
    }
  }, [tutorialQueue, currentTutorial, isPaused]);

  const continueTutorial = useCallback(() => {
    if (currentTutorial) {
      const finishingId = tutorialStepId(currentTutorial);
      if (finishingId === 'counter_drones') {
        shownTutorialsRef.current.add('protect_your_units');
      }
      console.log(`📚 useCampaignState: continueTutorial called for "${finishingId}"`);
      console.log(`📚 useCampaignState: Current queue length: ${tutorialQueue.length}`);
      console.log(`📚 useCampaignState: Current queue:`, tutorialQueue.map(t => tutorialStepId(t)));
      
      // Calculate next state first, then update all states together for smooth transition
      setTutorialQueue(prev => {
        // Remove the tutorial being dismissed by id — not queue[0]. advanceToTutorial can show
        // a step that also exists later in the queue (e.g. attack_the_general after out_of_reach).
        const newQueue = (finishingId
          ? prev.filter(t => tutorialStepId(t) !== finishingId)
          : prev.slice(1)
        ).filter(t => !isDeprecatedTutorial(t));
        console.log(`📚 useCampaignState: After removing current, new queue length: ${newQueue.length}`);
        console.log(`📚 useCampaignState: New queue:`, newQueue.map(t => tutorialStepId(t)));
        
        // ✅ FIX: If no tutorials in local queue, check backend queue for more
        if (newQueue.length === 0 && gameState?.scriptState?.tutorialQueue) {
          const backendTutorials = gameState.scriptState.tutorialQueue;
          console.log(`📚 useCampaignState: Local queue empty, checking backend queue (${backendTutorials.length} tutorial(s))`);
          
          const existingIds = new Set(prev.map(t => t.stepId || t.id || t.title));
          const toAdd = backendTutorials.filter(t => {
            const id = t.stepId || t.id || t.title;
            return !shownTutorialsRef.current.has(id)
              && id !== currentTutorialTitleRef.current
              && !DEPRECATED_TUTORIAL_STEP_IDS.has(id);
          });
          
          if (toAdd.length > 0) {
            console.log(`📚 useCampaignState: Found ${toAdd.length} tutorial(s) in backend queue, adding to local queue`);
            const updatedQueue = [...newQueue, ...toAdd];
            if (updatedQueue.length > 0) {
              const nextTutorial = updatedQueue[0];
              console.log(`📚 useCampaignState: Transitioning to next tutorial from backend:`, nextTutorial);
              setCurrentTutorial(nextTutorial);
              setIsPaused(true);
            }
            return updatedQueue;
          }
        }
        
        // Update current tutorial immediately if there's a next one to avoid unmount/remount
        if (newQueue.length > 0) {
          const nextTutorial = newQueue[0];
          console.log(`📚 useCampaignState: Transitioning directly to next tutorial:`, nextTutorial);
          // These updates will be batched together by React
          setCurrentTutorial(nextTutorial);
          setIsPaused(true);
        } else {
          // No more tutorials, clear current
          console.log(`📚 useCampaignState: No more tutorials in queue, clearing current tutorial`);
          setCurrentTutorial(null);
          setIsPaused(false);
        }
        return newQueue;
      });
    }
  }, [currentTutorial, tutorialQueue, gameState?.scriptState?.tutorialQueue]);

  const clearTutorials = useCallback(() => {
    setTutorialQueue([]);
    setCurrentTutorial(null);
    setIsPaused(false);
  }, []);

  const addTutorial = useCallback((tutorial) => {
    setTutorialQueue(prev => {
      const existingIds = new Set(prev.map(t => t.stepId || t.id || t.title));
      const id = tutorial.stepId || tutorial.id || tutorial.title;
      const currentTitle = currentTutorialTitleRef.current;
      // Skip if already in queue OR if it's the currently showing tutorial
      if (existingIds.has(id) || (currentTitle && id === currentTitle) || DEPRECATED_TUTORIAL_STEP_IDS.has(id)) {
        console.log(`📚 useCampaignState: Tutorial "${id}" already in queue or showing (current: ${currentTitle}), skipping`);
        return prev;
      }
      console.log(`📚 useCampaignState: Adding tutorial "${id}" to queue (current: ${currentTitle})`);
      return [...prev, tutorial];
    });
  }, []);

  /** Advance directly to a specific tutorial (e.g. when event provides next tutorial from backend) */
  const advanceToTutorial = useCallback((nextTutorial, options = {}) => {
    if (!nextTutorial) return;
    const nextId = tutorialStepId(nextTutorial);
    const currentId = tutorialStepId(currentTutorial);
    const skipIds = new Set(options.skipStepIds || []);
    if (currentId) skipIds.add(currentId);
    if (nextId) skipIds.add(nextId);
    console.log(`📚 useCampaignState: advanceToTutorial to "${nextId}"`);
    setTutorialQueue(prev => prev.filter(t => !skipIds.has(tutorialStepId(t))));
    setCurrentTutorial(nextTutorial);
    setIsPaused(true);
  }, [currentTutorial]);

  // Restore tutorial state (for save/load functionality)
  const restoreTutorialState = useCallback((tutorialState) => {
    if (tutorialState) {
      if (tutorialState.currentTutorial) {
        console.log(`📚 useCampaignState: Restoring current tutorial: ${tutorialState.currentTutorial.stepId || tutorialState.currentTutorial.title}`);
        setCurrentTutorial(tutorialState.currentTutorial);
        setIsPaused(true);
      }
      if (tutorialState.tutorialQueue && Array.isArray(tutorialState.tutorialQueue)) {
        console.log(`📚 useCampaignState: Restoring tutorial queue: ${tutorialState.tutorialQueue.length} tutorial(s)`);
        setTutorialQueue(tutorialState.tutorialQueue);
      }
    }
  }, []);

  return {
    currentTutorial,
    isPaused,
    continueTutorial,
    advanceToTutorial,
    clearTutorials,
    addTutorial,
    restoreTutorialState,
    hasTutorials: tutorialQueue.length > 0 || currentTutorial !== null
  };
}

